import type { QuestionVisibilityState } from "@/components/survey/Question";
import {
  DiabetesTypeOptionId,
  type DisqualificationReason,
  PregnantSelection,
} from "@/constants";
import { AppContextType } from "@/contexts";

import { getAge } from "../age";
import { calculateBMIImperial } from "../bmi";

export const DPP_INELIGIBLE_DIABETES_TYPES = [
  DiabetesTypeOptionId.TYPE1,
  DiabetesTypeOptionId.TYPE2,
  DiabetesTypeOptionId.UNSURE,
];

export const calculateDppRiskScore = (
  context: Pick<AppContextType, "surveyAnswers" | "retriageEligibility">,
) => {
  const {
    diabetesType,
    familyHistory,
    gestationalDiabetes,
    heightWeight,
    hypertension,
    physicalActivity,
    prediabetesHistory,
    sex,
  } = <
    {
      diabetesType: DiabetesTypeOptionId;
      familyHistory: string;
      gestationalDiabetes: string;
      heightWeight: {
        heightFeetPortion: number;
        heightInchesPortion: number;
        weight: number;
      };
      hypertension: string;
      physicalActivity: string;
      prediabetesHistory: string[];
      sex: string;
    }
  >context.surveyAnswers;

  let dppRiskScore = 0;

  const bmi = calculateBMIImperial(
    heightWeight?.heightFeetPortion,
    heightWeight?.heightInchesPortion,
    heightWeight?.weight,
  );

  if (bmi >= 23 && bmi < 30) {
    dppRiskScore += 1;
  } else if (bmi >= 30 && bmi < 40) {
    dppRiskScore += 2;
  } else if (bmi >= 40) {
    dppRiskScore += 3;
  } else {
    dppRiskScore += 0;
  }

  const age = getAge(context);
  if (!age) {
    dppRiskScore += 0;
  } else if (age >= 40 && age < 50) {
    dppRiskScore += 1;
  } else if (age >= 50 && age < 60) {
    dppRiskScore += 2;
  } else if (age >= 60) {
    dppRiskScore += 3;
  } else {
    dppRiskScore += 0;
  }

  dppRiskScore +=
    prediabetesHistory?.reduce(
      (acc, risk) =>
        acc +
        (["prediabetes", "a1c", "fastingBloodSugarLevel"].includes(risk)
          ? 5
          : 0),
      0,
    ) || 0;

  dppRiskScore += sex === "male" ? 1 : 0;
  dppRiskScore +=
    gestationalDiabetes === "yes" ||
    diabetesType === DiabetesTypeOptionId.GESTATIONAL
      ? 5
      : 0;
  dppRiskScore += hypertension === "yes" ? 1 : 0;
  dppRiskScore += familyHistory === "yes" ? 1 : 0;
  dppRiskScore += physicalActivity === "no" ? 1 : 0;

  return dppRiskScore;
};

/**
 * This function returns whether a user qualifies for DPP based on the
 * current visibility state.
 *
 * A user qualfies for DPP if they meet the following criteria:
 * - has a BMI >= 25 or BMI >= 23 and is of Asian ethnicity
 * - has an ideal BMI >= 18.5
 * - is not pregnant
 * - does not have Type 1, Type 2, or unsure diabetes
 * - scores at least 5 points on the DPP risk calculation
 *
 * This is used to determine question visibility so it does mean it will return
 * null if some questions have not yet been answered.
 *
 * @param {QuestionVisibilityState} state The entire visibility state
 * @returns {DisqualificationReason | null} Returns a disqualification reason
 * if the user does not qualify for DPP, null if the user qualifies
 */
export const qualifiesForDpp = ({
  eligibilityInfo: { eligiblePrograms = [] } = {},
  retriageEligibility,
  surveyAnswers,
}: QuestionVisibilityState): DisqualificationReason | null => {
  const {
    diabetesType,
    ethnicity,
    heightWeight: { heightFeetPortion, heightInchesPortion, weight } = {},
    physicalActivity,
    pregnant,
  } = surveyAnswers;

  const dppOffered =
    eligiblePrograms.includes("DPP") ||
    retriageEligibility?.eligibleRetriagePrograms?.includes("DPP");

  if (!dppOffered) {
    return "PROGRAM_NOT_OFFERED";
  }

  /**
   * If the user hasn't answered the height and weight question, they aren't in
   * a position to be excluded from DPP so we return true.
   */
  if (
    heightFeetPortion === undefined ||
    heightInchesPortion === undefined ||
    weight === undefined
  ) {
    return null;
  }

  const bmi = calculateBMIImperial(
    heightFeetPortion,
    heightInchesPortion,
    weight,
  );

  /**
   * First exclude users who have a BMI < 23 since the ethnicity question won't
   * change their qualification.
   */
  if (bmi < 23) {
    return "BMI_TOO_LOW";
  }

  /**
   * Exclude users who have BMI 23-25 and are not Asian. Users with BMI < 23
   * would have been excluded earlier.
   */
  if (
    ethnicity &&
    bmi < 25 &&
    (!ethnicity.includes("asian") || ethnicity.includes("preferNotToSay"))
  ) {
    return "BMI_TOO_LOW";
  }

  if (pregnant && pregnant === PregnantSelection.YES) {
    return "PREGNANT";
  }

  if (diabetesType && DPP_INELIGIBLE_DIABETES_TYPES.includes(diabetesType)) {
    return "INELIGIBLE_DIABETES_TYPE";
  }

  /**
   * Risk score is only calculated at the end of all DPP risk questions.
   * Physical activity is the last question in the set of DPP risk questions.
   */
  if (physicalActivity) {
    const dppRiskScore = calculateDppRiskScore({
      retriageEligibility,
      surveyAnswers,
    });

    if (dppRiskScore < 5) {
      return "DPP_RISK_SCORE_TOO_LOW";
    }
  }

  return null;
};
